<template>
  <InstructorLayout>
    <div class="instructor-day0">
      <h2 class="page-title">Day0ミニ体験</h2>
      <p class="page-desc">本登録前の学習者が体験で提出した音声に、フィードバックを録音して返信できます。</p>

      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <h3 class="section-title">Day0ミニ体験の提出</h3>
            <select v-model="day0StatusFilter" class="form-select" @change="loadDay0Submissions">
              <option value="submitted">未対応</option>
              <option value="feedback_sent">FB送信済み</option>
              <option value="no_reply_needed">返信不要</option>
              <option value="all">すべて</option>
            </select>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="day0Loading" @click="loadDay0Submissions">
              更新
            </button>
          </div>
          <div v-if="day0Loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!day0Submissions.length" class="empty-state">該当するDay0提出がありません</div>
          <div v-else class="table-wrap submissions-table-wrap">
            <table class="data-table" aria-label="Day0提出一覧">
              <thead>
                <tr>
                  <th>提出日時（JST）</th>
                  <th>お名前</th>
                  <th>属性</th>
                  <th class="num">回数</th>
                  <th>提出音声</th>
                  <th>ステータス</th>
                  <th>返信音声</th>
                  <th class="action">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in day0Submissions" :key="d.id" class="data-row">
                  <td>{{ formatDateTimeJst(d.submittedAt) }}</td>
                  <td>
                    {{ day0NameLabel(d) }}
                    <span v-if="day0KindLabel(d)" class="day0-voice-badge">{{ day0KindLabel(d) }}</span>
                  </td>
                  <td>{{ d.audience === 'child' ? 'お子さま' : '大人' }}</td>
                  <td class="num">
                    {{ d.attemptNumber }}
                    <span v-if="day0DurationLabel(d)" class="day0-duration">{{ day0DurationLabel(d) }}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-play"
                      :disabled="day0PlayingId === d.id && day0PlayingKind === 'submission'"
                      @click="playDay0Audio(d, 'submission')"
                    >
                      {{ day0PlayingId === d.id && day0PlayingKind === 'submission' ? '再生中...' : '▶ 提出' }}
                    </button>
                  </td>
                  <td>
                    <span class="status-badge" :class="d.status">{{ day0StatusText(d.status) }}</span>
                  </td>
                  <td>
                    <button
                      v-if="d.feedbackUrl"
                      type="button"
                      class="btn btn-sm btn-play"
                      :disabled="day0PlayingId === d.id && day0PlayingKind === 'feedback'"
                      @click="playDay0Audio(d, 'feedback')"
                    >
                      {{ day0PlayingId === d.id && day0PlayingKind === 'feedback' ? '再生中...' : '▶ 返信' }}
                    </button>
                    <span v-else class="no-audio">—</span>
                  </td>
                  <td class="action">
                    <div v-if="d.status === 'submitted'" class="action-buttons">
                      <button type="button" class="btn btn-sm btn-reply" @click="openDay0FeedbackModal(d)">
                        フィードバック録音
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-no-reply"
                        :disabled="day0NoReplyProcessingId === d.id"
                        @click="markDay0NoReply(d)"
                      >
                        {{ day0NoReplyProcessingId === d.id ? '処理中...' : '🚫 返信不要' }}
                      </button>
                    </div>
                    <span v-else class="no-action">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <audio
            ref="day0AudioEl"
            controls
            class="day0-inline-audio"
            @ended="onDay0AudioEnded"
            @pause="onDay0AudioEnded"
            @error="onDay0AudioEnded"
          />
        </div>
      </section>

      <!-- Day0フィードバック録音モーダル -->
      <div v-if="showDay0Modal" class="day0-modal-overlay" @click.self="closeDay0Modal">
        <div class="day0-modal-content">
          <h3>{{ day0FeedbackTitle(day0ModalTarget) }}</h3>
          <audio :src="day0ModalTarget?.recordingUrl" controls class="day0-modal-audio"></audio>

          <div v-if="!day0IsRecording && !day0RecordedBlob" class="day0-mode-toggle">
            <label>
              <input type="radio" value="audio" v-model="day0FeedbackMode" /> 音声
            </label>
            <label v-if="isVideoSupported">
              <input type="radio" value="video" v-model="day0FeedbackMode" /> 動画
            </label>
          </div>

          <p v-if="day0MicError" class="day0-modal-error">{{ day0MicError }}</p>

          <video
            v-if="!day0RecordedBlob && day0FeedbackMode === 'video'"
            ref="day0LivePreviewEl"
            muted
            autoplay
            playsinline
            class="day0-modal-video day0-modal-video-live"
          ></video>
          <p v-if="!day0RecordedBlob && day0FeedbackMode === 'audio' && !day0PreviewReady" class="day0-preview-note">
            マイクを準備しています...
          </p>
          <p v-if="day0IsRecording" class="day0-recording-timer">🔴 {{ formatDay0RecordingTime(day0RecordingSeconds) }}</p>

          <div class="day0-modal-actions">
            <button
              v-if="!day0IsRecording && !day0RecordedBlob"
              type="button"
              class="btn btn-primary"
              :disabled="!day0PreviewReady"
              @click="startDay0Feedback"
            >
              ●録音開始
            </button>
            <button
              v-else-if="day0IsRecording"
              type="button"
              class="btn btn-danger"
              @click="stopDay0Feedback"
            >
              ■停止
            </button>
            <template v-else>
              <video v-if="day0FeedbackMode === 'video'" :src="day0RecordedPreviewUrl" controls playsinline class="day0-modal-video"></video>
              <audio v-else :src="day0RecordedPreviewUrl" controls class="day0-modal-audio"></audio>
              <button type="button" class="btn btn-secondary" :disabled="day0Uploading" @click="resetDay0Recording">
                録り直す
              </button>
              <button type="button" class="btn btn-primary" :disabled="day0Uploading" @click="submitDay0Feedback">
                {{ day0Uploading ? '送信中...' : 'このままLINEへ送信' }}
              </button>
            </template>
          </div>
          <button type="button" class="btn btn-secondary day0-modal-close" @click="closeDay0Modal">閉じる</button>
        </div>
      </div>
    </div>
  </InstructorLayout>
</template>

<script setup>
import { day0NameLabel, day0KindLabel, day0DurationLabel, day0FeedbackTitle } from '../../utils/day0SubmissionView.js'
import { ref, onMounted, onUnmounted, watch } from 'vue'
import InstructorLayout from '../../components/layout/InstructorLayout.vue'
import { supabase } from '../../supabaseClient'
import { isVideoFeedbackSupported } from '../../utils/videoRecorder.js'
import { createDay0ChunkRecorder } from '../../composables/useDay0ChunkedRecorder.js'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

const isVideoSupported = isVideoFeedbackSupported()

// ===== Day0ミニ体験の提出（managerの提出管理画面と同じロジック・同じAPIを共有） =====
const day0Submissions = ref([])
const day0Loading = ref(false)
const day0StatusFilter = ref('submitted')
const day0NoReplyProcessingId = ref(null)

const showDay0Modal = ref(false)
const day0ModalTarget = ref(null)
const day0IsRecording = ref(false)
const day0Uploading = ref(false)
const day0MicError = ref('')
const day0RecordedBlob = ref(null)
const day0RecordedPreviewUrl = ref(null)
const day0FeedbackMode = ref('audio') // 'audio' | 'video'
const day0LivePreviewEl = ref(null)
const day0PreviewReady = ref(false)
const day0RecordingSeconds = ref(0)
let day0RecordingTimer = null
let day0RecordingId = null
let day0RecordingCodecType = null

// 本番の学習者提出・フィードバック録音と同じ「数秒ごとのチャンク分割→逐次アップロード」方式（詳しくはcomposable参照）
// codecTypeも本番と同様に送信する（動画は常にwebm。音声はiOS/SafariだとWebM非対応でmp4になる）。
const day0FeedbackRecorder = createDay0ChunkRecorder({
  onStreamReady: (stream) => {
    if (day0LivePreviewEl.value) day0LivePreviewEl.value.srcObject = stream
  },
  uploadChunk: async (chunkData, chunkInfo) => {
    const formData = new FormData()
    const ext = chunkInfo.codecType === 'mp4' ? 'mp4' : 'webm'
    formData.append('chunk', chunkData, `chunk_${chunkInfo.chunkIndex}.${ext}`)
    formData.append('recordingId', chunkInfo.recordingId)
    formData.append('chunkIndex', String(chunkInfo.chunkIndex))
    formData.append('codecType', chunkInfo.codecType || 'webm')
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions/${day0ModalTarget.value.id}/feedback-chunk`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    })
    const json = await res.json().catch(() => ({}))
    if (!res.ok || !json.success) throw new Error(json?.error || 'チャンクの送信に失敗しました')
  }
})

// 一覧の「▶ 提出」「▶ 返信」再生（ページ読み込み時に全件を読み込まず、押した時だけ再生する）
const day0AudioEl = ref(null)
const day0PlayingId = ref(null)
const day0PlayingKind = ref(null) // 'submission' | 'feedback'

function playDay0Audio(d, kind) {
  if (!day0AudioEl.value) return
  const url = kind === 'feedback' ? d.feedbackUrl : d.recordingUrl
  if (!url) return
  day0PlayingId.value = d.id
  day0PlayingKind.value = kind
  day0AudioEl.value.src = url
  day0AudioEl.value.play().catch(() => {
    day0PlayingId.value = null
    day0PlayingKind.value = null
  })
}

function onDay0AudioEnded() {
  day0PlayingId.value = null
  day0PlayingKind.value = null
}

function day0StatusText(status) {
  const map = { submitted: '未対応', feedback_sent: 'FB送信済み', no_reply_needed: '返信不要' }
  return map[status] || status
}

async function loadDay0Submissions() {
  day0Loading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions?status=${encodeURIComponent(day0StatusFilter.value)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.error || 'Day0提出の取得に失敗しました')
    day0Submissions.value = data.data || []
  } catch (e) {
    console.error('Day0提出取得エラー:', e)
  } finally {
    day0Loading.value = false
  }
}

async function markDay0NoReply(d) {
  if (day0NoReplyProcessingId.value) return
  day0NoReplyProcessingId.value = d.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions/${d.id}/no-reply`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.error || '更新に失敗しました')
    d.status = 'no_reply_needed'
  } catch (e) {
    alert(e.message || '更新に失敗しました')
  } finally {
    day0NoReplyProcessingId.value = null
  }
}

async function openDay0FeedbackModal(d) {
  day0ModalTarget.value = d
  day0MicError.value = ''
  day0RecordedBlob.value = null
  day0RecordedPreviewUrl.value = null
  day0RecordingId = null
  day0RecordingCodecType = null
  day0FeedbackMode.value = isVideoSupported ? 'video' : 'audio'
  showDay0Modal.value = true
  await prepareDay0Preview()
}

function closeDay0Modal() {
  if (day0IsRecording.value) return
  stopDay0RecordingTimer()
  day0FeedbackRecorder.cancelPreview()
  day0PreviewReady.value = false
  showDay0Modal.value = false
  day0ModalTarget.value = null
}

/** モーダルを開いた直後・モード切替時に呼ぶ。カメラ/マイクを先に確保し、録音開始前にプレビュー（自撮り映像）を出す */
async function prepareDay0Preview() {
  day0MicError.value = ''
  day0PreviewReady.value = false
  const isVideo = day0FeedbackMode.value === 'video'
  try {
    if (isVideo) {
      await day0FeedbackRecorder.prepareVideoPreview()
    } else {
      await day0FeedbackRecorder.prepareAudioPreview()
    }
  } catch (e) {
    day0MicError.value = isVideo
      ? 'カメラ/マイクへのアクセスが許可されませんでした。'
      : 'マイクへのアクセスが許可されませんでした。'
    return
  }
  day0PreviewReady.value = true
}

watch(day0FeedbackMode, () => {
  if (showDay0Modal.value && !day0IsRecording.value && !day0RecordedBlob.value) {
    prepareDay0Preview()
  }
})

function startDay0RecordingTimer() {
  day0RecordingSeconds.value = 0
  day0RecordingTimer = setInterval(() => {
    day0RecordingSeconds.value += 1
  }, 1000)
}

function stopDay0RecordingTimer() {
  if (day0RecordingTimer) {
    clearInterval(day0RecordingTimer)
    day0RecordingTimer = null
  }
}

function formatDay0RecordingTime(sec) {
  const m = String(Math.floor(sec / 60)).padStart(2, '0')
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

async function startDay0Feedback() {
  if (!day0PreviewReady.value) {
    day0MicError.value = 'カメラ/マイクの準備ができていません。少し待ってからもう一度お試しください。'
    return
  }
  try {
    await day0FeedbackRecorder.start(day0FeedbackMode.value)
  } catch (e) {
    day0MicError.value = '録音を開始できませんでした。'
    return
  }
  day0IsRecording.value = true
  startDay0RecordingTimer()
}

async function stopDay0Feedback() {
  day0IsRecording.value = false
  stopDay0RecordingTimer()
  day0PreviewReady.value = false

  try {
    const { recordingId, previewBlob, codecType } = await day0FeedbackRecorder.stop()
    day0RecordingId = recordingId
    day0RecordingCodecType = codecType
    if (previewBlob) {
      day0RecordedBlob.value = previewBlob
      day0RecordedPreviewUrl.value = URL.createObjectURL(previewBlob)
    }
  } catch (e) {
    day0MicError.value = '録音データの送信中にエラーが発生しました。もう一度お試しください。'
  }
}

function resetDay0Recording() {
  day0RecordedBlob.value = null
  if (day0RecordedPreviewUrl.value) URL.revokeObjectURL(day0RecordedPreviewUrl.value)
  day0RecordedPreviewUrl.value = null
  day0RecordingId = null
  day0RecordingCodecType = null
  prepareDay0Preview()
}

/** チャンクは録音中に既にアップロード済みなので、ここではサーバー側の結合・mp4変換・LINE送信を依頼するだけでよい */
async function submitDay0Feedback() {
  if (!day0RecordingId || !day0ModalTarget.value) return
  day0Uploading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/day0-submissions/${day0ModalTarget.value.id}/feedback-finalize`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ recordingId: day0RecordingId, mediaKind: day0FeedbackMode.value, codecType: day0RecordingCodecType })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || !data.success) throw new Error(data.error || '送信に失敗しました')

    const target = day0Submissions.value.find((x) => x.id === day0ModalTarget.value.id)
    if (target) target.status = 'feedback_sent'
    closeDay0Modal()
  } catch (e) {
    day0MicError.value = e.message || '送信に失敗しました'
  } finally {
    day0Uploading.value = false
  }
}

onMounted(() => {
  loadDay0Submissions()
})

onUnmounted(() => {
  stopDay0RecordingTimer()
  day0FeedbackRecorder.cancelPreview()
})
</script>

<style scoped>
.instructor-day0 {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 0.75rem 0;
  font-size: 1.1rem;
  color: #334155;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-select {
  padding: 0.4rem 0.6rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.submissions-table-wrap {
  max-height: 70vh;
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.6rem;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
}

.data-table th.num,
.data-table td.num {
  text-align: right;
}

.data-table thead th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-row:hover {
  background: #f8fafc;
}

.status-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

.status-badge.submitted {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.no_reply_needed {
  background: #e5e7eb;
  color: #4b5563;
}

.btn-play {
  font-size: 0.8rem;
}

.no-audio {
  color: #94a3b8;
}

.data-table th.action,
.data-table td.action {
  white-space: nowrap;
}

.btn-reply {
  font-size: 0.8rem;
}

.btn-no-reply {
  font-size: 0.8rem;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.action-buttons {
  display: flex;
  gap: 0.4rem;
}

.no-action {
  color: #94a3b8;
}

.day0-inline-audio {
  max-width: 220px;
  height: 32px;
}

.day0-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.day0-modal-content {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 480px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.day0-modal-audio {
  width: 100%;
}

/* 撮影中の表示・撮影後のチェックとも、サーバーの変換（中央を縦長3:4に切り抜く）と同じ範囲を見せる。
   カメラの映像・手元の録画は切り抜く前なので、3:4の枠からはみ出る部分を中央で切って表示する（データ自体は変えない） */
.day0-modal-video {
  display: block;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  margin: 0 auto;
  background: #000;
  border-radius: 8px;
}

/* 自撮りのライブプレビューだけ鏡のように左右反転する（実際に保存される映像データ自体は反転しない） */
.day0-modal-video-live {
  transform: scaleX(-1);
}

.day0-mode-toggle {
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
}

.day0-preview-note {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.day0-recording-timer {
  font-size: 1.1rem;
  font-weight: bold;
  color: #dc2626;
  margin: 0;
}

.day0-modal-actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.day0-modal-error {
  color: #dc2626;
  font-size: 0.85rem;
  margin: 0;
}

.day0-modal-close {
  align-self: flex-end;
}

.day0-voice-badge {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: #ede9fe;
  color: #5b21b6;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.day0-duration {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
}
</style>
